<template>
  <div class="h-100 d-flex flex-column align-center justify-space-around fit-content mx-auto ga-5">
    <ConnectingLine
      :startElementId="`command-${system.index}-${event.index}`"
      :endElementId="`system-${system.index}`"
      :refreshKey="refreshKey"
      offsetParentId="domain-model"
    />
    <div :id="`command-${system.index}-${event.index}`" ref="commandEl">
      <div
        ref="cardElement"
        class="model-container"
        v-if="system.actions[event.index]"
        :style="{
          minHeight: getCardHeight(event, system.actions[event.index]),
          marginTop: !showEntities ? '2px' : '20px'
        }"
      >
        <div
          class="requirement-card hover-container height-transition-hover-container"
          :style="{
            'border-color': cardTypeColor(commandCardType),
            '--transitionContainerHeight': system.actions[event.index].dataModel.originalIndex === -1 ? '24px' : '16px'
          }"
        >
          <beam-card
            v-if="system.actions[event.index].dataModel.originalIndex === -1"
            :event="event"
            :card="system.actions[event.index]"
          />
          <div v-else>
            <div>
              <text-editor
                class="pa-1"
                :id="system.actions[event.index].id"
                :text="system.actions[event.index].description"
                :disabled="store.state.isWorkflowDisabled"
                @text-update="(e) => updateCardDescription(e, system.actions[event.index].id)"
                @move-focus="moveFocus"
                @keydown.native.stop=""
                :borderColor=" cardTypeColor(commandCardType)"
              />
            </div>
            <div
              v-if="system.actions[event.index].dataModel.dataFields.length > 0 && showEntities"
              ref="fieldsRef"
              class="py-2 fields"
              :data-event-id="event.id"
              :data-model-index="system.actions[event.index].dataModel.originalIndex"
            >
              <template v-for="(field, index4) in system.actions[event.index].dataModel.dataFields" :key="field.name">
                <div class="sortableRow">
                  <div class="field-name d-flex align-center">
                    <v-tooltip location="bottom" open-delay="500">
                      <template v-slot:activator="{ props }">
                        <v-btn
                          v-bind="getMatchingFieldInEntity(currentSystemEntitiesData?.rootEntity, field.name)?.reference ? props : undefined"
                          class="border-none cursor-handle sortHandle"
                          size="small"
                          variant="plain"
                          tabindex="-1"
                          :icon="getMatchingFieldInEntity(currentSystemEntitiesData?.rootEntity, field.name)?.reference ? 'mdi-link' : 'mds:drag-indicator'"
                          :disabled='store.state.isWorkflowDisabled'
                        />
                      </template>
                      <span>Reference to entity</span>
                    </v-tooltip>
                    <quick-rename-input
                      :id="`write-model-${event.id}-field-${index4}`"
                      :value="field.name"
                      @onChange="(e) => blurDataField(e, event, system.actions[event.index].dataModel.originalIndex, index4)"
                      :density="`compact`"
                      :max-length="30"
                      style="flex-grow: 1;"
                      :disabled="store.state.isWorkflowDisabled"
                    />
                    <v-chip
                      v-if="field.relatedEntityId && getRelatedEntity(field)"
                      density="compact"
                      size="small"
                      :closable="!store.state.isWorkflowDisabled"
                      class="related-entity-chip px-2 ml-1"
                    >
                      {{ field.cardinality === 'one-to-many'
                        ? `[ ${getRelatedEntity(field)?.name} ]`
                        : `${getRelatedEntity(field)?.name}` }}
                      <template v-if="!store.state.isWorkflowDisabled" #close>
                        <v-icon
                          icon="mdi-close-circle"
                          color="#888"
                          :size="16"
                          class="remove-entity-icon"
                          @click.stop="onSelectRelatedEntity(event, system.actions[event.index].id, index4, null)"
                        />
                      </template>
                    </v-chip>
                    <!-- Hidden in Form -->
                    <v-tooltip v-if="field.hideInForm" location="right" max-width="300px">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" icon="mdi-eye-off-outline" color="#888" :size="16" />
                      </template>
                      <span>Hidden from input form</span>
                    </v-tooltip>
                    <!-- Warning icon with tooltip -->
                    <v-tooltip v-if="isMissingEntity(field.name)" location="right" max-width="300px">
                      <template v-slot:activator="{ props }">
                        <span v-bind="props" style="cursor: default;">⚠️</span>
                      </template>
                      <span>This field on the command has no corresponding entity field.</span>
                    </v-tooltip>
                    <v-tooltip
                      v-else-if="isSelectedEntityMismatch(field)"
                      location="bottom"
                      max-width="300px"
                      open-delay="100"
                    >
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="14px" icon="mdi-alert" color="#ED1C0C" />
                      </template>
                      <span>On this command, the field '{{ field?.name }}' is set to be and entity of type '{{ getRelatedEntity(field)?.name }}'. This does not match with how the field '{{ field?.name }}' is defined under Aggregate Root.</span>
                    </v-tooltip>
                    <v-menu v-if="!store.state.isWorkflowDisabled" class="card-menu">
                      <template v-slot:activator="{ props }">
                        <v-btn
                          icon="mdi-dots-vertical"
                          size="small"
                          variant="plain"
                          class="write-model-field-actions-btn"
                          v-bind="props"
                        />
                      </template>
                      <v-list density="compact">
                        <select-entity
                          :eventId="event.id"
                          @onSelectEntity="(entity) => onSelectRelatedEntity(event, system.actions[event.index].id, index4, entity)"
                        >
                          <v-list-item class="cursor-pointer">
                            <v-list-item-title>
                              <v-icon class="mr-2" color="grey">mdi-database</v-icon> Select Entity
                            </v-list-item-title>
                          </v-list-item>
                        </select-entity>
                        <v-list-item @click="hideDataField(event, null, index4)">
                          <v-list-item-title>
                            <v-icon class="mr-2" color="grey" icon="mdi-eye-off-outline" /> {{ field.hideInForm ? 'Show' : 'Hide' }} in form
                          </v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="removeDataField(event, system.actions[event.index].id, index4)">
                          <v-list-item-title>
                            <v-icon class="mr-2" color="grey" icon="mdi-trash-can-outline" /> Delete
                          </v-list-item-title>
                        </v-list-item>
                        <v-divider></v-divider>
                        <div class="d-flex justify-center pt-1">
                          <v-tooltip location="bottom">
                            <template v-slot:activator="{ props }">
                              <toggle
                                v-bind="!field.relatedEntityId ? props : undefined"
                                mandatory
                                :value="field.cardinality || 'one-to-one'"
                                :options="[
                                  { value: 'one-to-one', label: '1 : 1' },
                                  { value: 'one-to-many', label: '1 : N' }
                                ]"
                                :disabled="!field.relatedEntityId"
                                @update:modelValue="(cardinality) => selectFieldCardinality(event, index4, cardinality)"
                              />
                            </template>
                            <span>Disabled because this field is not set to be a related entity.</span>
                          </v-tooltip>
                        </div>
                      </v-list>
                    </v-menu>
                  </div>
                  <div
                    v-if="field.relatedEntityId"
                    ref="subFieldsRef"
                    :data-event-id="event.id"
                    :data-model-index="system.actions[event.index].dataModel.originalIndex"
                    :data-field-index="index4"
                  >
                    <div
                      v-for="(item, index5) in field.dataFields"
                      :key="item.name"
                      class="pl-4 field-name d-flex align-center sortableRow"
                    >
                      <v-btn
                        class="border-0 cursor-handle sortHandle"
                        size="small"
                        variant="plain"
                        tabindex="-1"
                        icon="mds:drag-indicator"
                        :disabled='store.state.isWorkflowDisabled'
                      />
                      <quick-rename-input
                        :id="`write-model-${event.id}-field-${index4}-entity-${index5}`"
                        :value="item.name"
                        :density="`compact`"
                        @onChange="(e) => blurSubDataField(e, event, system.actions[event.index].id, index4, index5)"
                        :max-length="30"
                        style="flex-grow: 1;"
                        :disabled="store.state.isWorkflowDisabled"
                      />
                      <!-- Warning icon with tooltip -->
                      <v-tooltip
                        v-if="getMatchingFieldInEntity(currentSystemEntitiesData?.rootEntity, field.name)?.reference || isNameNotInEntity(field.relatedEntityId, item.name)"
                        max-width="380px"
                        location="right"
                      >
                        <template v-slot:activator="{ props }">
                          <span v-bind="props" style="cursor: default;">⚠️</span>
                        </template>
                        <span v-if="getMatchingFieldInEntity(currentSystemEntitiesData?.rootEntity, field.name)?.reference">
                          The field "{{ item.name }}" is set to be a reference to a separate aggregate, which
                          cannot be updated by this command. Therefore, nested fields here likely don't make sense.
                        </span>
                        <span v-else>This field on the command has no corresponding <br /> entity field.</span>
                      </v-tooltip>
                      <v-tooltip v-if="!store.state.isWorkflowDisabled" location="bottom" open-delay="100">
                        <template v-slot:activator="{ on, props }">
                          <v-btn
                            class="border-0 btn-delete"
                            v-bind="props"
                            v-on="{ ...on }"
                            size="small"
                            variant="plain"
                            tabindex="-1"
                            icon="mdi-trash-can-outline"
                            @click="removeSubDataField(event, system.actions[event.index].id, index4, index5)"
                          />
                        </template>
                        <span>Delete</span>
                      </v-tooltip>
                    </div>
                    <div
                      v-if="!store.state.isWorkflowDisabled && !getMatchingFieldInEntity(currentSystemEntitiesData?.rootEntity, field.name)?.reference"
                      class="d-flex align-center pl-4 btn-add-sub-field"
                    >
                      <v-tooltip location="bottom" open-delay="100">
                        <template v-slot:activator="{ on, props }">
                          <v-btn
                            v-bind="props"
                            v-on="{ ...on }"
                            size="small"
                            variant="plain"
                            @mousedown="onAddSubField(event, system.actions[event.index].id, index4)"
                            tabindex="-1"
                            icon="mdi-plus"
                          />
                        </template>
                        <span>Add field</span>
                      </v-tooltip>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
          <domain-model-card-toolbar
            v-if="!store.state.isWorkflowDisabled"
            :showAi="system.actions[event.index].dataModel.originalIndex === -1"
            :showFilters="true"
            :event="event"
            :card="system.actions[event.index]"
            :menuPositioningActivator="cardElement"
          />
        </div>
      </div>
      <v-btn
        v-else-if="commandCardType"
        class="add-card-btn"
        variant="elevated"
        :style="{ '--border-color': cardTypeColor(commandCardType) }"
        block
        @click="createCard(event.id, commandCardType.id, `<p>Command for ${event.description}</p>`)"
        :disabled="store.state.isWorkflowDisabled"
      >
        + {{ commandCardType.title }}
      </v-btn>
      <aggregate-type-selector
        v-else
        :requirementTypes="requirementTypes"
        type="command"
      />
    </div>
  </div>
</template>

<script setup>
import { useStore } from 'vuex'
import { computed, nextTick, onBeforeUnmount, onMounted, watch, ref } from 'vue'
import { useRoute } from 'vue-router'
import Toggle from '@/components/Toggle.vue'
import BeamCard from '@/components/BeamCard'
import TextEditor from '@/components/TextEditor.vue'
import SelectEntity from '@/components/SelectEntity.vue'
import ConnectingLine from '@/components/ConnectingLine'
import QuickRenameInput from '@/components/QuickRenameInput'
import AggregateTypeSelector from '@/components/AggregateTypeSelector'
import DomainModelCardToolbar from '@/components/DomainModelCardToolbar'
import workflowApi from '@/api/workflowApi'
import { EventBus } from '@/event-bus/event-bus'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import useDomainModel from '@/composables/useDomainModel'
import { useSortFields } from '@/composables/useSortFields'

const {
  system,
  event,
  refreshKey,
  currentSystemEntitiesData,
  showEntities
} = defineProps({
  system: Object,
  event: Object,
  refreshKey: Number,
  currentSystemEntitiesData: Object,
  showEntities: Boolean
})

const store = useStore()
const route = useRoute()

const workflow = computed(() => store.state.workflow.workflow)
const requirementTypes = computed(() => store.state.requirementTypes.requirementTypes)
const commandCardType = computed(() => diagramHelpers.methods.getCommandCardType(requirementTypes.value))

const {
  cardTypeColor,
  getCardHeight,
  getRelatedEntity,
  isNameNotInEntity,
  addSubField,
  blurDataField,
  blurSubDataField,
  removeSubDataField,
  onSelectRelatedEntity,
  removeDataField,
  getMatchingFieldInEntity,
  hideDataField,
  createCard
} = useDomainModel()
const { fieldsRef, subFieldsRef, initSortable, destroySortable } = useSortFields()

const commandEl = ref(null)
const cardElement = ref(null)

const selectFieldCardinality = (event, index, cardinality) => {
  const commandIndex = event.dataModels.findIndex((dataModel) => dataModel.type === 'Event Story')
  if (commandIndex === -1) {
    return
  }

  const callback = (event) => {
    event.dataModels[commandIndex].dataFields[index].cardinality = cardinality
  }
  EventBus.$emit('update-data-model-with-function', { updateFunction: callback, eventId: event.id || route.params.eventId })
}

const isMissingEntity = (commandField) => {
  if (!currentSystemEntitiesData.rootEntity) {
    return false
  }
  if (!currentSystemEntitiesData) {
    return true
  }

  const rootFields = currentSystemEntitiesData.rootEntity
    ? currentSystemEntitiesData.rootEntity.dataFields.map((field) => field.name)
    : []

  const entityFields = currentSystemEntitiesData.entities
    ? currentSystemEntitiesData.entities.flatMap((entity) => entity.dataFields.map((field) => field.name))
    : []

  const availableFields = new Set([...rootFields, ...entityFields])

  return !availableFields.has(commandField)
}

const moveFocus = (_, target) => {
  target.firstChild.blur()
}

const updateCardDescription = (html, itemId) => {
  workflowApi.updateRequirementsJsonMutation(route.params.id, [{ requirementId: itemId, description: html }])
}

const isSelectedEntityMismatch = (field) => {
  const matchingFieldInEntity = getMatchingFieldInEntity(currentSystemEntitiesData?.rootEntity, field.name)

  if (!matchingFieldInEntity || field.relatedEntityId === matchingFieldInEntity?.relatedEntityId) {
    return false
  }

  // Checking for actual entity existence
  const commandFieldRelatedEntity = field.relatedEntityId
    ? workflow.value.dataModels?.find((entity) => entity.id === field.relatedEntityId)
    : true
  const entityFieldRelatedEntity = matchingFieldInEntity?.relatedEntityId
    ? workflow.value.dataModels?.find((entity) => entity.id === matchingFieldInEntity?.relatedEntityId)
    : true

  return !!commandFieldRelatedEntity && !!entityFieldRelatedEntity
}

const onAddSubField = (...params) => {
  if (document.activeElement?.id?.startsWith('combobox-input-')) {
    // Prevent race condition
    return
  }

  addSubField(...params)
}

watch(() => system.events, () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      initSortable()
    })
  })
})

onMounted(() => {
  initSortable()
})

onBeforeUnmount(() => {
  destroySortable()
})
</script>

<style scoped>
.textEditor.ql-editor.ql-container {
  min-height: 31px;
}

.card-menu .v-list-item:hover{
  background-color: var(--light-2) !important;
}

.v-menu > .v-overlay__content > .v-list {
  padding: 4px 0 !important;
}

.sortable-chosen {
  background: #eee;
}

.sortable-ghost {
  background: #E5E3E1;
}

.sortable-ghost > div {
  opacity: 0.01;
}

.sortableRow:hover .btn-add-sub-field {
  opacity: 1;
  height: 16px;

  .v-btn {
    opacity: 0.7;

    &:hover {
      opacity: 1;
    }
  }
}

.btn-add-sub-field {
  opacity: 0;
  height: 0;
  transition: all 0.2s;
}
</style>
